/**
 * Where to find the certificate for a certification row.
 *
 * Shared by the dashboard's "My Assigned Certifications" table and the
 * /my-certifications page, which render the same getMyCertifications() payload.
 * They disagreed before: the dashboard offered a View link while
 * /my-certifications showed an em dash for every row in the platform.
 *
 * Three things decide the answer, and each of them was got wrong at least once:
 *
 *  1. Every custom course has a certificate, module-based or PDF-only. That was
 *     not always true: the endpoint answered 409 NOT_MODULAR for a PDF-only
 *     course until backend #88, and the error page's only way out pointed at
 *     the player, which refused it for the same reason. Two of the four courses
 *     in use are PDF-only and hold 22 of the 30 completions, so the broken case
 *     was the majority.
 *
 *  2. The LEGACY viewer is addressed by the base64 of the course TITLE, not by
 *     any id. Base64 can contain '/', which would be read as another path
 *     segment and never match :type — "Is Your Center Ready?" encodes to a
 *     string ending "UmVhZHk/" — so it has to be URL-encoded. The viewer calls
 *     atob() on an already-decoded param, so that is transparent to it.
 *
 *  3. The OWNER comes from the row, not from whoever is looking. A school admin
 *     reading this list sees their OWN certifications, but the user object
 *     stored in the browser at school level is the SCHOOL — so building the
 *     link from localStorage asks for a certificate belonging to a school id.
 *
 * Deliberately NOT gated on cert.certificateUrl. That field is null on all
 * 2,221 completed certifications, so gating on it hides the link from everyone;
 * it is why /my-certifications has been showing an em dash for every row.
 * Certificates here are rendered on demand from the certification record rather
 * than served from a stored file.
 *
 * @param {object} cert    a row from getMyCertifications()
 * @param {string} [fallbackUserId]  used only when the row predates teacherId
 * @returns {string|null}  a route, or null when there is no certificate to show
 */
export const certificateLinkFor = (cert, fallbackUserId) => {
  if (!cert) return null;

  const status = String(cert.status || '').toLowerCase();
  if (status !== 'completed' && status !== 'approved') return null;

  const key = String(cert.courseKey || '');
  if (key.startsWith('custom-training-')) {
    // Both shapes now. A PDF-only course is read and then passed in one quiz;
    // the certificate endpoint used to answer 409 NOT_MODULAR for one, which is
    // why this returned null. It issues them as of backend #88, and the two
    // PDF-only courses in use hold 22 of the 30 completions.
    return `/custom-training/certificate/${key.replace('custom-training-', '')}`;
  }

  const title = cert.courseTitle || cert.courseKey;
  const ownerId = cert.teacherId || fallbackUserId;
  if (!title || !ownerId) return null;

  // btoa throws on anything outside Latin-1, and course titles are
  // author-entered — a stray curly quote would otherwise take the page down
  // rather than just this one link.
  try {
    return `/viewcertificate/${ownerId}/${encodeURIComponent(btoa(title))}`;
  } catch (e) {
    return null;
  }
};

/** The id to fall back to when a row predates teacherId. */
export const storedUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u._id || u.id || null;
  } catch (e) {
    return null;
  }
};

export default certificateLinkFor;
